import {
  Controller,
  useFieldArray,
  useWatch,
  type Control,
  type FieldErrors,
  type UseFormRegister,
} from 'react-hook-form';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { SelectField } from '@/components/forms/SelectField';
import { TextField } from '@/components/forms/TextField';
import { useSectors } from '@/features/startups/hooks/useSectors';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import type { InvestorProfileData } from '../../api/investor-profile.api';
import type { InvestorProfileFormValues } from './schema';

const S = UI_STRINGS.INVESTOR_PROFILE;

interface ColleagueContactsSectionProps {
  mode: 'view' | 'edit';
  data: InvestorProfileData;
  control?: Control<InvestorProfileFormValues>;
  register?: UseFormRegister<InvestorProfileFormValues>;
  errors?: FieldErrors<InvestorProfileFormValues>;
}

/**
 * Colleague contacts by industry (MYN-202, FR-175): one named colleague per
 * sector, repeatable rows modelled on `GeographiesSection`. The sector list is
 * the platform `Sector` taxonomy (the vocabulary the export matches on), NOT
 * the investor industry-preference list above it. Contact-only: saving a row
 * creates no account and sends no email.
 */
export function ColleagueContactsSection({
  mode,
  data,
  control,
  register,
  errors,
}: ColleagueContactsSectionProps) {
  const { data: sectors = [] } = useSectors();

  if (mode === 'view' || !control || !register) {
    const contacts = data.colleagueContacts ?? [];
    return (
      <Card title={S.COLLEAGUE_CONTACTS} subtitle={S.COLLEAGUE_CONTACTS_HINT} headerDivider hoverEffect={false}>
        {contacts.length === 0 ? (
          <p className="text-p6 text-slate">—</p>
        ) : (
          <ul className="flex flex-col divide-y divide-divider">
            {contacts.map((contact) => (
              <li
                // Not `contact.id`: the Backend regenerates row ids on every save.
                // One colleague per sector, so the sector id is a stable unique key.
                key={contact.sectorId}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 py-3 text-p6 first:pt-0 last:pb-0"
              >
                <span className="font-semibold text-navy">
                  {sectors.find((sector) => sector.id === contact.sectorId)?.name ?? '—'}
                </span>
                <span className="text-navy">{contact.name}</span>
                <span className="text-slate">{contact.email}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    );
  }

  return (
    <ColleagueContactsEditor
      control={control}
      register={register}
      errors={errors}
      sectorOptions={sectors.map((sector) => ({ value: String(sector.id), label: sector.name }))}
    />
  );
}

interface SectorOption {
  value: string;
  label: string;
}

function ColleagueContactsEditor({
  control,
  register,
  errors,
  sectorOptions,
}: {
  control: Control<InvestorProfileFormValues>;
  register: UseFormRegister<InvestorProfileFormValues>;
  errors?: FieldErrors<InvestorProfileFormValues>;
  sectorOptions: SectorOption[];
}) {
  const { fields, append, remove } = useFieldArray({ control, name: 'colleagueContacts' });
  const rows = useWatch({ control, name: 'colleagueContacts' }) ?? [];
  const usedSectorIds = rows.map((row) => (row?.sectorId ? String(row.sectorId) : ''));
  // Nothing left to add once every sector has a row (once the list has loaded).
  const allSectorsUsed =
    sectorOptions.length > 0 && sectorOptions.every((option) => usedSectorIds.includes(option.value));

  return (
    <Card title={S.COLLEAGUE_CONTACTS} subtitle={S.COLLEAGUE_CONTACTS_HINT} headerDivider hoverEffect={false}>
      <div className="flex flex-col gap-4">
        {fields.map((field, index) => {
          const rowErrors = errors?.colleagueContacts?.[index];
          // A sector chosen in ANOTHER row is not selectable here; this row's
          // own pick stays in its list so it renders.
          const options = sectorOptions.filter(
            (option) => !usedSectorIds.some((used, i) => i !== index && used === option.value),
          );
          return (
            <div
              key={field.id}
              role="group"
              aria-label={S.COLLEAGUE_ROW(index + 1)}
              className="rounded-card bg-page-bg p-4"
            >
              <div className="mb-3 flex items-center justify-between">
                <span className="text-p6 font-semibold text-navy">
                  {S.COLLEAGUE} {index + 1}
                </span>
                <Button
                  type="button"
                  variant="link"
                  onClick={() => remove(index)}
                  aria-label={`${S.REMOVE} ${S.COLLEAGUE} ${index + 1}`}
                  className="text-p6 text-danger hover:text-danger"
                >
                  {S.REMOVE}
                </Button>
              </div>
              <div className="flex flex-col gap-3">
                {/* Controller (not register): the options load asynchronously, and an
                    uncontrolled native select applies its initial value only on mount. */}
                <Controller
                  control={control}
                  name={`colleagueContacts.${index}.sectorId`}
                  render={({ field: sectorField }) => (
                    <SelectField
                      label={S.COLLEAGUE_SECTOR}
                      aria-label={S.COLLEAGUE_ROW_FIELD(S.COLLEAGUE_SECTOR, index + 1)}
                      placeholder={S.COLLEAGUE_SECTOR_PLACEHOLDER}
                      value={sectorField.value ? String(sectorField.value) : ''}
                      onChange={(event) =>
                        sectorField.onChange(event.target.value ? Number(event.target.value) : undefined)
                      }
                      onBlur={sectorField.onBlur}
                      options={options}
                      error={rowErrors?.sectorId?.message}
                    />
                  )}
                />
                <div className="grid gap-3 sm:grid-cols-2">
                  <TextField
                    {...register(`colleagueContacts.${index}.name`)}
                    label={S.COLLEAGUE_NAME}
                    aria-label={S.COLLEAGUE_ROW_FIELD(S.COLLEAGUE_NAME, index + 1)}
                    error={rowErrors?.name?.message}
                  />
                  <TextField
                    {...register(`colleagueContacts.${index}.email`)}
                    label={S.COLLEAGUE_EMAIL}
                    aria-label={S.COLLEAGUE_ROW_FIELD(S.COLLEAGUE_EMAIL, index + 1)}
                    type="email"
                    error={rowErrors?.email?.message}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <Button
        type="button"
        variant="dashed"
        disabled={allSectorsUsed}
        // sectorId's schema type is a required number, but a brand-new row starts
        // with no selection; required-ness is still enforced at submit (see the
        // same note in GeographiesSection).
        onClick={() => append({ sectorId: undefined as never, name: '', email: '' })}
        className="mt-4 inline-flex w-full items-center justify-center gap-2"
      >
        {S.ADD_COLLEAGUE}
      </Button>
    </Card>
  );
}
